import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Chip,
  Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatDate as formatDatePt } from '../../utils/date';
import { getProductsByLocation, ProductLoteShelfDto } from '../../services/expensesApi';

const ProductLocationList: React.FC = () => {
  const [productsByLocation, setProductsByLocation] = useState<Record<string, ProductLoteShelfDto[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProductsByLocation = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getProductsByLocation();
      setProductsByLocation(data);
    } catch (e: any) {
      setError(e.message || 'Não foi possível carregar os produtos por local');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    fetchProductsByLocation(); 
  }, []);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/D';
    return formatDatePt(dateString);
  };

  const getExpirationColor = (dateString?: string) => {
    if (!dateString) return 'default';
    const expDate = new Date(dateString);
    const today = new Date();
    const diffDays = Math.ceil((expDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
    
    if (diffDays < 0) return 'error'; // Expired
    if (diffDays <= 7) return 'warning'; // Expires soon
    if (diffDays <= 30) return 'info'; // Expires in a month
    return 'success'; // Good expiration
  };

  if (loading) {
    return <Typography>A carregar produtos...</Typography>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const locationNames = Object.keys(productsByLocation);

  if (locationNames.length === 0) {
    return <Typography>Nenhum produto encontrado em nenhum local.</Typography>;
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Produtos por local ({locationNames.length} locais)
      </Typography>
      
      {locationNames.map((locationName) => {
        const products = productsByLocation[locationName];
        const totalQuantity = products.reduce((sum, product) => sum + product.quantity, 0);
        
        return (
          <Accordion key={locationName} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6">{locationName}</Typography>
                <Chip 
                  label={`${products.length} produtos`} 
                  size="small" 
                  color="primary" 
                />
                <Chip 
                  label={`Total: ${totalQuantity} unidades`} 
                  size="small" 
                  color="secondary" 
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nome do produto</TableCell>
                      <TableCell>Prateleira</TableCell>
                      <TableCell>Número do lote</TableCell>
                      <TableCell>Quantidade</TableCell>
                      <TableCell>Data de validade</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.map((product) => (
                      <TableRow key={product.publicId}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {product.productName}
                          </Typography>
                        </TableCell>
                        <TableCell>{product.shelfName}</TableCell>
                        <TableCell>{product.loteNumber || '-'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={product.quantity} 
                            size="small" 
                            color={product.quantity > 0 ? 'success' : 'error'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={formatDate(product.expirationDate)} 
                            size="small" 
                            color={getExpirationColor(product.expirationDate)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default ProductLocationList;
